import { useTokens } from './tokens';

/**
 * KbdHint — chip visual estilo `<kbd>` que indica um atalho de teclado.
 *
 * Aceita string `"Ctrl+F"` ou array `["↑", "↓"]`. Strings com `+` são
 * quebradas em teclas separadas com `+` entre elas. Cada tecla vira um
 * "chip" elevado/sombrado igual aos kbd hints do VSCode/macOS.
 *
 * Props:
 *   keys     — string | string[] : atalho a exibir
 *   label    — string opcional : descrição depois das teclas ("Buscar")
 *   compact  — boolean : versão menor (default false)
 *   onClick  — opcional, se quiser tornar clickável
 */
export function KbdHint({ keys, label, compact = false, onClick, title }) {
    const T = useTokens();
    const list = normalizeKeys(keys);
    const keyStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: compact ? 16 : 20,
        height: compact ? 16 : 20,
        padding: compact ? '0 4px' : '0 6px',
        fontSize: compact ? 10 : 11,
        fontFamily: T.mono,
        fontWeight: 600,
        color: T.inkMid,
        background: T.bgSoft,
        border: `1px solid ${T.line}`,
        borderRadius: 4,
        boxShadow: `0 1px 0 ${T.lineMid}`,
        lineHeight: 1,
        userSelect: 'none',
    };
    const sepStyle = {
        color: T.muted,
        fontSize: compact ? 9 : 10,
        margin: '0 2px',
        userSelect: 'none',
    };
    const containerStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        cursor: onClick ? 'pointer' : 'default',
    };

    const content = (
        <>
            {list.map((part, i) => {
                if (part === '+') {
                    return (
                        // biome-ignore lint/suspicious/noArrayIndexKey: separadores não têm id semântico
                        <span key={`sep-${i}`} style={sepStyle}>
                            +
                        </span>
                    );
                }
                return (
                    // biome-ignore lint/suspicious/noArrayIndexKey: posição é estável dentro da string de atalho
                    <kbd key={`kbd-${i}`} style={keyStyle}>
                        {part}
                    </kbd>
                );
            })}
            {label && <span style={{ color: T.muted, fontSize: compact ? 10 : 11, marginLeft: 4 }}>{label}</span>}
        </>
    );

    if (onClick) {
        return (
            <button
                type="button"
                onClick={onClick}
                title={title}
                style={{ ...containerStyle, background: 'transparent', border: 'none', padding: 0 }}
            >
                {content}
            </button>
        );
    }
    return (
        <span style={containerStyle} title={title}>
            {content}
        </span>
    );
}

function normalizeKeys(keys) {
    if (Array.isArray(keys)) return keys;
    if (typeof keys === 'string') {
        // "Ctrl+F" → ["Ctrl", "+", "F"]
        return keys
            .split(/(\+)/)
            .map((s) => s.trim())
            .filter(Boolean);
    }
    return [];
}
